import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { UserRole } from '@/domain';
import { apiGet, apiPost, refreshSession, setAccessToken, setAuthFailureHandler } from '@/lib/api';

/**
 * Session state for the SPA.
 *
 * Task 04 owns the shape and the guards; Task 05 completes the endpoints behind them.
 * The access token lives in memory (see lib/api.ts) — on a page reload the app asks
 * /auth/refresh whether the httpOnly cookie still represents a live session.
 */

export interface CurrentUser {
  id: number;
  fullName: string;
  email: string;
  role: UserRole;
  mustChangePassword: boolean;
}

interface AuthContextValue {
  user: CurrentUser | null;
  /** True until the initial session probe settles — routes must wait, not redirect. */
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<CurrentUser>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  /** FRD §3.2 — the UI courtesy layer. The API is the actual enforcement point. */
  can: (...roles: UserRole[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface LoginResponse {
  accessToken: string;
  user: CurrentUser;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setUser(null);
  }, []);

  // A refresh that fails after the app has loaded means the session is genuinely over
  // (expired, logged out elsewhere, or the account was deactivated — FR-AUTH-010).
  useEffect(() => {
    setAuthFailureHandler(clearSession);
    return () => setAuthFailureHandler(null);
  }, [clearSession]);

  // Session probe on first load: the cookie may still be valid from a previous visit.
  //
  // This goes through the shared single-flight `refreshSession`, not a bare POST. The
  // server rotates the refresh token on every use, so a second concurrent probe would
  // present an already-consumed token and be refused — and React 18 StrictMode invokes
  // this effect twice in development, making that collision happen on every load.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const session = await refreshSession();
        if (!cancelled) {
          setUser(session ? session.user : null);
        }
      } catch {
        if (!cancelled) clearSession();
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clearSession]);

  const login = useCallback(async (email: string, password: string): Promise<CurrentUser> => {
    const result = await apiPost<LoginResponse>('/auth/login', { email, password });
    setAccessToken(result.accessToken);
    setUser(result.user);
    return result.user;
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await apiPost('/auth/logout');
    } finally {
      // Even if the server call fails, the local session ends.
      clearSession();
    }
  }, [clearSession]);

  const refreshUser = useCallback(async (): Promise<void> => {
    const currentUser = await apiGet<CurrentUser>('/auth/me');
    setUser(currentUser);
  }, []);

  const can = useCallback(
    (...roles: UserRole[]): boolean => {
      if (!user) return false;
      if (roles.length === 0) return true;
      return roles.includes(user.role);
    },
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: user !== null,
      login,
      logout,
      refreshUser,
      can,
    }),
    [user, isLoading, login, logout, refreshUser, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/*
 * The hook is deliberately co-located with its provider — splitting a context and its
 * consumer hook across files makes both harder to follow. The cost is that this module
 * exports a non-component, which limits fast refresh to a full reload for this file only.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
